import React, { useCallback, useEffect, useMemo, useRef, } from 'react';
import { Menu, MenuItem, Tooltip } from '@doxyme/ui';
import { openCallSettingsFromMenu } from '../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { getIsCaptionsOpen, toggleClosedCaptionsOpen } from '../../../captions';
import { openBackgroundEffectsModal } from '../../../core/actions';
import * as troubleshooting from '../../../troubleshooting';
import { useIntl } from 'react-intl';
import { noop } from 'lodash';
import { utils, hasGrowthBookFeature, GrowthBookFeatures, canSeeBackgroundEffects, } from '../../../core';
import './CallMenu.scss';
const translationsKeyMap = {
    settings: 'call.actions.options.items.settings',
    troubleshooting: 'call.actions.options.items.troubleshooting',
    backgroundEffects: 'backgroundEffects.title',
    backgroundEffectsTooltip: 'backgroundEffects.tooltip',
    closedCaptions: 'call.actions.options.items.closedCaptions',
};
const AccessibleMenuItem = ({ text, icon, onSelect, ariaLabel, disabled = false, }) => {
    const activate = () => {
        if (disabled) {
            return;
        }
        onSelect();
    };
    const handleKeyDown = (e) => {
        if (disabled) {
            return;
        }
        // Native <button> elements already trigger click on Enter and (on keyup)
        // on Space, but JAWS users reported that Space did not always activate
        // items in this menu. We handle both explicitly so behaviour is identical
        // across screen readers and keyboards.
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
        }
    };
    return (<button type="button" className="call-menu__item" role="menuitem" tabIndex={disabled ? -1 : 0} aria-label={ariaLabel} aria-disabled={disabled} onClick={activate} onKeyDown={handleKeyDown}>
      <MenuItem text={text} icon={icon} disabled={disabled}/>
    </button>);
};
export const CallMenu = ({ isPatient, toggleMenu = noop, }) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const containerRef = useRef(null);
    const localized = useMemo(() => utils.formatObjectValues(translationsKeyMap, (value) => intl.formatMessage({ id: value })), []);
    const isBackgroundEffectsEnabled = useMemo(() => canSeeBackgroundEffects(), []);
    const isClosedCaptionsOpen = useSelector(getIsCaptionsOpen);
    const onBackgroundEffects = useCallback(() => {
        toggleMenu(false);
        dispatch(openBackgroundEffectsModal('call menu'));
    }, []);
    const onTroubleshooting = useCallback(() => {
        toggleMenu(false);
        dispatch(troubleshooting.actions.openTroubleshooting());
    }, [dispatch, toggleMenu]);
    const onClosedCaptions = () => {
        toggleMenu(false);
        dispatch(toggleClosedCaptionsOpen(!isClosedCaptionsOpen, isPatient));
    };
    const onCallSettings = useCallback(() => {
        toggleMenu(false);
        dispatch(openCallSettingsFromMenu(isPatient));
    }, [isPatient]);
    useEffect(() => {
        const firstItem = containerRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
        firstItem?.focus();
    }, []);
    const focusItemByOffset = (offset) => {
        const items = [
            ...(containerRef.current?.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])') ?? []),
        ];
        if (items.length === 0) {
            return;
        }
        const active = document.activeElement;
        const currentIndex = active ? items.indexOf(active) : -1;
        const nextIndex = currentIndex === -1
            ? offset > 0
                ? 0
                : items.length - 1
            : (currentIndex + offset + items.length) % items.length;
        items[nextIndex]?.focus();
    };
    const onContainerKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            toggleMenu(false);
            return;
        }
        // Arrow keys move focus between menu items so screen-reader users can
        // navigate without Tabbing out of the menu. Mirrors the WAI-ARIA menu
        // pattern (https://www.w3.org/WAI/ARIA/apg/patterns/menubar/).
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            focusItemByOffset(1);
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            focusItemByOffset(-1);
            return;
        }
        if (e.key === 'Home') {
            e.preventDefault();
            e.stopPropagation();
            const first = containerRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
            first?.focus();
            return;
        }
        if (e.key === 'End') {
            e.preventDefault();
            e.stopPropagation();
            const all = containerRef.current?.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])');
            const last = all?.[all.length - 1];
            last?.focus();
        }
    };
    return (<div ref={containerRef} className="call-menu__items" role="menu" onKeyDown={onContainerKeyDown}>
      <Menu>
        {isBackgroundEffectsEnabled && (<Tooltip size="medium" theme="light" pointerPosition="top-center" positionShift={8} isInteractive={false} borderRadius={16} isDefaultTriggerPadding={false} content={localized.backgroundEffectsTooltip}>
            <AccessibleMenuItem text={localized.backgroundEffects} icon="visual-effects" onSelect={onBackgroundEffects} ariaLabel={localized.backgroundEffects}/>
          </Tooltip>)}
        {hasGrowthBookFeature(GrowthBookFeatures.ClosedCaptions) && (<AccessibleMenuItem text={localized.closedCaptions} icon="closed-captions" onSelect={onClosedCaptions} ariaLabel={localized.closedCaptions} disabled={isClosedCaptionsOpen}/>)}
        <AccessibleMenuItem text={localized.troubleshooting} icon="troubleshooting" onSelect={onTroubleshooting} ariaLabel={localized.troubleshooting}/>
        <AccessibleMenuItem text={localized.settings} icon="call-settings" onSelect={onCallSettings} ariaLabel={localized.settings}/>
      </Menu>
    </div>);
};
